import React, { useState, useEffect, useRef } from "react";
import {
    Box,
    Typography,
    TextField,
    Button,
    Paper,
    Avatar,
    Chip,
    IconButton,
    CircularProgress,
    Divider,
    Alert,
    LinearProgress,
    Fade,
    AppBar,
    Toolbar,
    Tooltip,
    Modal
} from "@mui/material";
import {
    Send as SendIcon,
    Refresh as RefreshIcon,
    CheckCircle as CheckCircleIcon,
    Close as CloseIcon,
    Person as PersonIcon,
    AttachFile as AttachFileIcon,
    HelpOutline as HelpIcon,
    QuestionAnswer as QuestionIcon
} from "@mui/icons-material";

import { EnhancedChatbotEngine } from "../utils/enhancedChatbotEngine";
import { ProjectSetupData } from "./AIProjectSetupModal";
import Component250418IntIcon from "../imports/250418IntIcon-13029-85";
import { CompactFileUpload } from "./common/CompactFileUpload";

import { useSelector, useDispatch } from "react-redux";
import { AppDispatch, RootState } from "@/state/store";

import { useAppSelector, useAppDispatch } from "@/state/hooks";
// Use enhanced client with proper SSE buffering to fix JSON parsing errors
import { EnhancedAgentClient } from "@/api/agentClient";
import { ChatInterface } from "@intelligenic/chat-components";
import { UserProjectSetup } from "./UserProjectSetup";

const BACKEND_URI = import.meta.env.VITE_API_BASE_URL || "";

// Enhanced client buffers SSE chunks before parsing JSON
const client = new EnhancedAgentClient({
    baseURL: import.meta.env.VITE_AGENT_URL,
    apiKey: import.meta.env.VITE_AGENT_KEY,
    timeout: 30000 // optional
});

const agentConfig = {
    customerOnboarding: {
        id: "customer-onboarding",
        name: "Customer Onboarding",
        description: "Help customers get started",
        endpoint: `${BACKEND_URI}/api/agents/customer-onboarding`,
        // UI-specific configs
        avatar: "👋",
        color: "#1954ED"
    }
};

interface EnhancedProjectSetupChatbotProps {
    open: boolean;
    onClose?: () => void;
    onRefresh?: () => void;
}

export const EnhancedProjectSetupChatbot: React.FC<EnhancedProjectSetupChatbotProps> = ({ open, onClose, onRefresh }) => {
    const [chatSessionId, setChatSessionId] = useState<string | null>(null);
    const [showSetup, setShowSetup] = useState(false);
    const [projectInfo, setProjectInfo] = useState<any>(null);
    const [resetKey, setResetKey] = useState(0);
    const token = useAppSelector(state => state.clientToken.token);
    const userId = useAppSelector(state => state.userProfiles.userProfile?.userid);

    // Reset state when modal closes
    useEffect(() => {
        if (!open) {
            // Reset all state when modal is closed
            setChatSessionId(null);
            setProjectInfo(null);
            setShowSetup(false);
            // Increment key to force ChatInterface remount on next open
            setResetKey(prev => prev + 1);
        }
    }, [open]);

    // Debug logging for ChatInterface dependencies
    useEffect(() => {
        console.log("[EnhancedProjectSetupChatbot] Auth state check:", {
            hasToken: !!token,
            tokenLength: token?.length,
            userId: userId,
            userIdType: typeof userId,
            isOpen: open
        });
    }, [token, userId, open]);

    // Check if we have required auth data
    const isAuthReady = !!token && userId !== undefined && userId !== null;

    if (!isAuthReady && open) {
        console.warn("[EnhancedProjectSetupChatbot] Waiting for auth data:", {
            token: token ? "present" : "missing",
            userId: userId ?? "missing"
        });
    }

    return (
        <>
            {/* Only render UserProjectSetup when actually needed to prevent unnecessary mounting and useEffect execution */}
            {showSetup && (
                <UserProjectSetup
                    isOpen={showSetup}
                    onClose={() => {
                        setShowSetup(false);
                        onClose && onClose();
                        onRefresh && onRefresh();
                    }}
                    projectInfo={projectInfo}
                />
            )}
            <Modal
                open={open}
                onClose={(event, reason) => {
                    // Only allow closing via the X button (not backdrop click or escape key)
                    if (reason === "backdropClick" || reason === "escapeKeyDown") {
                        return;
                    }
                    onClose && onClose();
                }}
                disableEscapeKeyDown
                aria-labelledby="onboarding-chat-modal"
                aria-describedby="interactive-onboarding-chat-interface"
                sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center"
                }}
            >
                <Box
                    sx={{
                        width: { xs: "95vw", sm: "90vw", md: "80vw", lg: "70vw", xl: "60vw" },
                        maxWidth: "1200px",
                        height: { xs: "90vh", sm: "85vh", md: "80vh" },
                        maxHeight: "900px",
                        bgcolor: "background.paper",
                        borderRadius: 2,
                        boxShadow: 24,
                        display: "flex",
                        flexDirection: "column",
                        overflow: "hidden",
                        outline: "none"
                    }}
                >
                    {/* Modal Header with Title and Close Button */}
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            px: 3,
                            py: 2,
                            borderBottom: 1,
                            borderColor: "divider",
                            bgcolor: "background.paper",
                            flexShrink: 0
                        }}
                    >
                        <Typography variant="h6" component="h2" sx={{ fontWeight: 600, color: "text.primary" }}>
                            Add New Product
                        </Typography>
                        <IconButton
                            onClick={onClose}
                            sx={{
                                color: "text.secondary",
                                "&:hover": {
                                    bgcolor: "action.hover",
                                    color: "text.primary"
                                }
                            }}
                        >
                            <CloseIcon />
                        </IconButton>
                    </Box>

                    {/* Chat content area - constrained to remaining space */}
                    <Box
                        sx={{
                            flex: 1,
                            minHeight: 0,
                            overflow: "hidden",
                            display: "flex",
                            flexDirection: "column"
                        }}
                    >
                        {!isAuthReady ? (
                            <Box
                                sx={{
                                    display: "flex",
                                    flexDirection: "column",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    height: "100%",
                                    gap: 2
                                }}
                            >
                                <CircularProgress />
                                <Typography variant="body2" color="text.secondary">
                                    Loading authentication...
                                </Typography>
                            </Box>
                        ) : (
                            <ChatInterface
                                key={`onboarding-chat-interface-${resetKey}`}
                                agentType="onboarding"
                                sessionId={chatSessionId}
                                onSessionCreate={setChatSessionId}
                                streamMode={true}
                                apiClient={client}
                                welcomeMessage={`Intelligenic`}
                                placeholder="..."
                                userId={userId}
                                securityToken={token}
                                fileUploadUrl={BACKEND_URI}
                                onComplete={data => {
                                    console.log("Chat completed:", data.project_info);
                                    setProjectInfo(data.project_info);
                                    setShowSetup(true);
                                }}
                                onClose={() => {
                                    //alert("CLOSED");
                                    //setShowSetup(true);
                                }}
                            />
                        )}
                    </Box>
                </Box>
            </Modal>
        </>
    );
};

export default EnhancedProjectSetupChatbot;
